import Sidebar from '@pelckmans/elementis/components/sidebar';
import List from '@pelckmans/elementis/components/list';
import { ComponentProps, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Node } from '../../api/table-of-content';
import { useMaterialLinkBuilder } from '../../hooks/useMaterialLinkBuilder';
import ModuleContext from '../../pages/material/_contexts/ModuleContext';
import TableOfContentsContext from '../../pages/material/toc/_contexts/TableOfContentsContext';
import AppButtonList from '../../pages/material/toc/components/toc-page-header/AppButtonList';
import ModuleBreadcrumbs from '../header/ModuleBreadcrumbs';

interface MappedNode extends Node {
  level: number;
}

function containsNode(node: Node, id: string): boolean {
  if (node.id === id) return true;
  return node.nodes?.some(child => containsNode(child, id)) ?? false;
}

export default function MaterialSidebar({
  overview,
  setOverviewMode,
}: {
  overview: boolean;
  setOverviewMode: (isOverivew: boolean) => void;
}) {
  const { t } = useTranslation();
  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode, nodes } = useContext(TableOfContentsContext);
  const { buildMaterialTocLink } = useMaterialLinkBuilder();

  const getAppearance = (node: MappedNode) => {
    if (node.id === selectedNode?.id) return 'active';

    return node.level === 0 ? 'prominent' : 'default';
  };

  const getNodeAvatar = (node: MappedNode) => {
    if (node.id === 'general') {
      return {
        name: selectedModule?.niceName || 'placeholder',
        title: selectedModule?.niceName || 'placeholder',
        src: selectedModule?.styling?.logo || 'placeholder',
      };
    }

    return node.level === 0
      ? {
          name: node.title,
          title: node.title,
          src: node.image || 'placeholder',
        }
      : undefined;
  };

  const onActiveClick = useCallback(() => {
    setOverviewMode(false);
  }, [setOverviewMode]);

  const renderNodes = (nodes: Node[], level = 0, parentPath: string[] = []): (JSX.Element | null)[] => {
    const childIsActive = selectedNode && nodes.some(n => containsNode(n, selectedNode?.id));

    return nodes.flatMap(node => {
      if (
        parentPath.length &&
        !(childIsActive || node.id === selectedNode?.id || parentPath[parentPath.length - 1] === selectedNode?.id)
      )
        return null;

      const newParentPath = [...parentPath, node.id];
      const mappedNode = { ...node, level };

      return [
        <List.Item
          key={node.id}
          avatar={getNodeAvatar(mappedNode)}
          title={node.id === 'general' ? t('TOC.GENERAL') : node.title}
          subText={node.displayPrefix ? node.prefix : undefined}
          subTextPlacement="top"
          level={mappedNode.level as ComponentProps<typeof List.Item>['level']}
          as="link"
          href={buildMaterialTocLink(node.id) || ''}
          onClick={node.id === selectedNode?.id ? onActiveClick : undefined}
          appearance={getAppearance(mappedNode)}
        />,
        ...(node.nodes ? renderNodes(node.nodes, level + 1, newParentPath) : []),
      ];
    });
  };

  return (
    <Sidebar
      overview={overview}
      onOverviewChange={selectedNode?.id ? (isOverview: boolean) => setOverviewMode(isOverview) : undefined}
    >
      <Sidebar.Header
        breadcrumbs={<ModuleBreadcrumbs size="sm" />}
        headerText={t('PAGES.TOC_LEVEL.SIDEBAR.TITLE')}
        buttonGroupProps={{
          'aria-label': undefined,
          role: 'group',
          children: <AppButtonList density="normal" digibooksOnly={true} />,
        }}
      />
      <Sidebar.Content>
        <List showSeparators={false} isLoading={!selectedModule || !nodes.length} loadingLabel={t('LIST.LOADING')}>
          {renderNodes(nodes)}
        </List>
      </Sidebar.Content>
    </Sidebar>
  );
}
